import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { useWindowSize } from '../../../../../utils/hooks';
import { responsiveSizeTimesheet, validationMessages } from '../../../../../utils/constants';
import EditTimesheetForm from '../../../timesheet/MainTable/EditTimesheetModal/components/EditTimesheetForm';
import PerDiemCreationComponent from '../../../../common/PerDiemCreationComponent/PerDiemCreationComponent';
import classes from './HoursStep.module.scss';

import EditTimesheetFormFullMonth from '../../../timesheet/MainTable/EditMonthlyTimesheetDrawer/component/EditTimesheetFormFullMonth';
import { rateEnum, submissionEnum } from '../../../../../utils/enums';

export default function HoursStep({
  timesheet,
  setTimesheet,
  allowedWorkTypes,
  choosedMonth,
  timesheetType,
  rateType,
  wrapperContainer,
  perdiems = [],
  changePerDiem
}) {
  const { t } = useTranslation();
  const size = useWindowSize();
  const [error, setError] = useState('');

  const validationSchema = yup.object({
    Location: yup.string('Enter location').required(validationMessages(t).LOCATION_REQ),
    WorkTypeId: yup.number('Enter work type').required(validationMessages(t).WORKTYPE_REQ)
  });

  const timesheetForm = useFormik({
    initialValues: timesheet,
    validationSchema: size.width > responsiveSizeTimesheet ? validationSchema : undefined,
    validateOnBlur: false,
    onSubmit: (values) => setTimesheet(values)
  });

  const handleWhenCleared = () => {
    timesheetForm.submitForm();
  };

  useEffect(() => {
    timesheetForm.submitForm();
  }, [timesheetForm.values.Location, timesheetForm.values.WorkTypeId, timesheetForm.values.TimesheetHoursByDates]);

  useEffect(() => {
    timesheetForm.values.TimesheetHoursByDates.some((tsItem) => {
      const matchingItem = perdiems.find((elem) => dayjs(tsItem.Day).isSame(dayjs(elem.Date)));
      if (Number(tsItem.TotalHrs) === 0 && matchingItem?.IsPerDiem) {
        changePerDiem(dayjs(matchingItem.Date), false);
        return true;
      }
      return false;
    });
  }, [timesheetForm.values.TimesheetHoursByDates]);

  useEffect(() => {
    if (timesheetForm.errors.Location) {
      setError(t('dashboard.createtimesheet.validations.enterlocation'));
      return;
    }
    if (timesheetForm.errors.WorkTypeId) {
      setError(t('dashboard.createtimesheet.validations.enterworktype'));
      return;
    }
    if (Object.keys(timesheetForm.errors).length === 0) setError('');
  }, [timesheetForm.errors, timesheetForm.values.Location, timesheetForm.values.WorkTypeId]);

  const wrapperClassForMobile = `${classes.timesheetContainer} ${wrapperContainer}`;

  return (
    <div
      onBlur={timesheetForm.submitForm}
      className={`${classes.container} ${timesheetType === submissionEnum.MONTHLY ? classes.withoutPadding : ''}`}
    >
      {timesheetType === submissionEnum.WEEKLY ? (
        <>
          <p className={classes.text}>
            {rateType === rateEnum.DAILY &&
              timesheetType === submissionEnum.WEEKLY &&
              t('dashboard.createtimesheet.steps.enterdays')}
            {rateType === rateEnum.HOURLY &&
              timesheetType === submissionEnum.WEEKLY &&
              t('dashboard.createtimesheet.steps.enterhours')}
          </p>
          <div className={size.width > responsiveSizeTimesheet ? classes.timesheetContainer : wrapperClassForMobile}>
            <EditTimesheetForm
              formData={timesheetForm}
              timesheetType={timesheetType}
              allowedWorkTypes={allowedWorkTypes}
              startDateOfPeriod={timesheet?.TimesheetHoursByDates?.[0].Day}
              choosedMonth={choosedMonth}
              rateType={rateType}
              withErrors={false}
              handleWhenCleared={handleWhenCleared}
              extraComponent={
                <PerDiemCreationComponent
                  perdiems={perdiems}
                  changePerdiem={changePerDiem}
                  startDateOfPeriod={timesheet?.TimesheetHoursByDates?.[0].Day}
                  choosedMonth={choosedMonth}
                  timesheetType={timesheetType}
                />
              }
            />
          </div>
        </>
      ) : (
        <div className={classes.monthlyContainer}>
          <div className={classes.auxContainer}>
            <EditTimesheetFormFullMonth
              formData={timesheetForm}
              allowedWorkTypes={allowedWorkTypes}
              choosedMonth={choosedMonth}
              rateType={rateType}
              handleWhenCleared={handleWhenCleared}
              timesheetType={timesheetType}
            />
          </div>
        </div>
      )}
      {size.width > responsiveSizeTimesheet && error && <p className={classes.error}>{error}</p>}
      {size.width > responsiveSizeTimesheet && !error && <p className={classes.error}>&nbsp;</p>}
    </div>
  );
}
